import { useMemo } from 'react';
import { useApiWithRetry } from '../hooks/useApiWithRetry';

/**
 * Tipos para a API de secrets
 */
export interface SecretKey {
  key: string;
}

export interface SecretCreate {
  key: string;
  value: string;
}

export interface SecretUpdate {
  value: string;
}

export interface SecretResponse {
  message: string;
  key: string;
  success: boolean;
}

export interface TenantSecrets {
  tenant: string;
  secrets: SecretKey[];
}

/**
 * Serviço para gerenciar secrets
 */
export class SecretsService {
  private api: ReturnType<typeof useApiWithRetry>;

  constructor(api: ReturnType<typeof useApiWithRetry>) {
    this.api = api;
  }

  /**
   * Lista todos os secrets de um tenant
   */
  async getTenantSecrets(tenant: string): Promise<TenantSecrets> {
    try {
      return await this.api.get<TenantSecrets>(`tenants/${tenant}/secrets`);
    } catch (error) {
      console.error('Erro ao carregar secrets:', error);
      throw new Error(error instanceof Error ? error.message : 'Erro desconhecido');
    }
  }

  /**
   * Cria ou atualiza um secret
   */
  async createOrUpdateSecret(
    tenant: string,
    secretData: SecretCreate
  ): Promise<SecretResponse> {
    try {
      return await this.api.post<SecretResponse>(`tenants/${tenant}/secrets`, secretData as unknown as Record<string, unknown>);
    } catch (error) {
      console.error('Erro ao salvar secret:', error);
      throw new Error(error instanceof Error ? error.message : 'Erro desconhecido');
    }
  }

  /**
   * Atualiza apenas o valor de um secret existente
   */
  async updateSecretValue(
    tenant: string,
    key: string,
    secretUpdate: SecretUpdate
  ): Promise<SecretResponse> {
    try {
      return await this.api.put<SecretResponse>(`tenants/${tenant}/secrets/${key}`, secretUpdate as unknown as Record<string, unknown>);
    } catch (error) {
      console.error('Erro ao atualizar secret:', error);
      throw new Error(error instanceof Error ? error.message : 'Erro desconhecido');
    }
  }

  /**
   * Deleta um secret
   */
  async deleteSecret(
    tenant: string,
    key: string
  ): Promise<SecretResponse> {
    try {
      return await this.api.delete<SecretResponse>(`tenants/${tenant}/secrets/${key}`);
    } catch (error) {
      console.error('Erro ao deletar secret:', error);
      throw new Error(error instanceof Error ? error.message : 'Erro desconhecido');
    }
  }
}

/**
 * Hook para usar o serviço de secrets
 */
export const useSecretsService = () => {
  const api = useApiWithRetry();
  return useMemo(() => new SecretsService(api), []); // Usar useMemo para evitar recriação
};
